import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStrings } from '../lib/strings';
import type { GradeResult } from '../types';

/**
 * Noticing that a learner is stuck, and offering the way out before they take
 * the other one.
 *
 * The product has had help since the first level: five rungs, from a question
 * to the answer, behind a button. What it has never had is the judgement to
 * offer it. A learner who does not know that `git init` is two words the
 * other way round does not think "I should open the hint ladder" — they think
 * the machine is refusing them, and the next thing they do is close the tab.
 * That is the failure this file exists to catch, and it is worth being exact
 * about what it may cost: a nudge that arrives while someone is thinking is
 * an interruption, and an interruption is its own reason to leave. So the
 * thresholds below are deliberately past the point of doubt, every nudge is
 * one dismissible line, and no nudge ever appears twice.
 *
 * What it watches, all of it already in the tab:
 *
 *   - checks whose failing objectives are identical to the last one's. Three
 *     of those is the clearest signal in the product: the learner is working,
 *     grading, and changing nothing the grader can see.
 *   - checks whose failing objectives alternate, A then B then A. The same
 *     despair wearing motion: one thing fixed, another broken, back again.
 *     The identical-set counter reads that as progress and says nothing.
 *   - commands that exited non-zero, in a row, and as a share of the last
 *     few. The shell reports this on ttyS2 (sandbox/vm/bash_profile); five in
 *     a row is flailing, and so is four of the last six, which is what
 *     flailing actually looks like once a learner runs an `ls` in between.
 *     Both are the only signals that fire for a learner who never presses
 *     Check.
 *   - silence. Minutes on a level with nothing passing and nothing typed is
 *     the shape of someone who has already given up and not yet said so.
 *   - never starting. Two minutes on a level with nothing typed, saved or
 *     checked at all. Measured 2026-10-10 on level 0: of the twelve browsers
 *     that started it and never cleared it, eight left inside eight minutes
 *     without one check, hint or word to Iris, and every signal above needs
 *     one of those (or six minutes) before it can speak. Those who opened
 *     Iris on that level cleared it 80% of the time and those who did not 23%,
 *     so on a level with a scripted Iris the offer is her first step.
 *
 * It used to watch one more thing, and the absence is worth a line. When the
 * engine matched a *situation* — a named wrong state a level was authored to
 * recognize — the offer changed from a rung of the ladder to a press of the
 * free deterministic mentor, which could name that state for nothing. That
 * mentor is retired (docs/mentor.md), so there is no free thing left to offer
 * and the signal has nothing to change. Matching still happens, on the level
 * screen and for a different reason: a recognized state is what makes the
 * first rungs of the ladder free (`free_through`), and that path never came
 * through here.
 *
 * Nothing here reads what was typed. The tab never sees the learner's
 * keystrokes as commands (web/src/terminal.ts), and a detector that needed
 * them would be a detector that could not run in the browser at all.
 */

export type NudgeId = 'not-started' | 'never-checked' | 'same-result' | 'error-streak' | 'gone-quiet' | 'ladder-spent';

export interface Nudge {
  id: NudgeId;
  /** One line, in the product's voice. Never more than one. */
  text: string;
  /**
   * 'check' grades what is there now, which is free and says which part is
   * not done; 'hint' opens the next rung and does not charge for it; 'skip'
   * leaves the level behind. In that order, deliberately: the cheapest help
   * the product has is the help it offers first, and `accept` counts a free
   * hint for 'hint' and for nothing else. 'iris' opens Iris on her first step,
   * on a level that has a scripted Iris; she proposes and the learner decides,
   * so it costs nothing and gives nothing away.
   */
  action: 'check' | 'hint' | 'skip' | 'iris';
  actionLabel: string;
}

/** Everything the decision is made from, so the decision itself is a pure function. */
export interface StuckSignals {
  /** Milliseconds since the level opened. */
  elapsedMs: number;
  /** Milliseconds since the last command, editor save or check. */
  idleMs: number;
  /** Failing checks in a row whose failing objectives were the same ones. */
  repeatedChecks: number;
  /**
   * Failing checks in a row whose failing objectives alternate: this check's
   * set differs from the last one's and equals the one before it. Three is
   * A, B, A.
   */
  alternatingChecks: number;
  /** Commands in a row that exited non-zero. */
  errorStreak: number;
  /**
   * Commands in the recent window whose exit status is known, and how many of
   * them failed. The window is short and the denominator is carried with the
   * numerator because a rate without one is not a rate: two failures out of
   * two is a learner who has typed twice.
   */
  recentCommands: number;
  recentFailures: number;
  /** Checks run on this level. Zero is its own kind of stuck. */
  checks: number;
  /** Anything at all on this level: a command finished, a file saved, a check. */
  started: boolean;
  /** What a learner who has not started is offered: Iris's first step where the level scripts her, else the first rung. */
  firstStep: 'iris' | 'hint';
  /**
   * Whether the learner has ever moved the grader: more objectives passing
   * than at their first check. Not "anything passes", because plenty of
   * levels have an objective that is already true when the level opens, and
   * counting that as progress would silence the signal on exactly those.
   */
  madeProgress: boolean;
  hintsUsed: number;
  hintCount: number;
  /** The level is already cleared: there is nothing to be stuck on. */
  cleared: boolean;
  /** Nudges the learner has already seen. Each one is offered once. */
  seen: readonly NudgeId[];
}

/**
 * Three checks, not two. Two identical results is a learner who tried one
 * thing and it did not work, which is the loop the product is made of; three
 * is a learner who has run out of things to try.
 */
const REPEATED_CHECKS = 3;

/**
 * Three checks that alternate — A, B, A — which is the shortest sequence in
 * which a learner can demonstrably arrive back where they started.
 *
 * `repeatedChecks` counts an *identical* failing set, and that is a narrower
 * reading of despair than it looks: a learner who fixes the first objective
 * and breaks the second, then fixes the second and breaks the first, has a
 * different failing set every single time and is scored as making progress on
 * every single one. They are not. They are holding one end of a rope down and
 * watching the other end come up, which is a worse place to be than repeating
 * yourself, because it feels like movement.
 *
 * Two is not enough for the same reason two identical checks is not: A then B
 * is one thing tried and a second thing tried, which is the loop the product
 * is made of. The third check is the one that says the loop is closed.
 */
const ALTERNATING_CHECKS = 3;

/**
 * The alternation run after one more check, given the two before it.
 *
 * Exported and pure for the reason `decideNudge` is: this is a policy about
 * what counts as going round in a circle, and a policy hidden inside a
 * callback is a policy nothing can argue with. `failing` is the current set
 * of failing objective ids as one string, `last` the previous check's and
 * `previous` the one before that; the empty string is a check that failed
 * nothing.
 *
 * The run starts at `ALTERNATING_CHECKS` rather than climbing from one,
 * because the first check that can possibly show an alternation is already
 * the third: there is no such thing as an alternation of two.
 */
export function nextAlternating(run: number, failing: string, last: string, previous: string): number {
  if (failing === '' || failing === last || failing !== previous) return 0;
  return run >= ALTERNATING_CHECKS ? run + 1 : ALTERNATING_CHECKS;
}

/**
 * Five failed commands in a row. A learner exploring a repository trips one
 * or two (a path that does not exist, a flag in the wrong place) without
 * being stuck at all; five is the state the screenshot showed — the same
 * wrong idea, typed again with a small variation.
 */
const ERROR_STREAK = 5;

/**
 * Four failures out of the last six commands.
 *
 * The streak above resets on any command that worked, and that is the shape
 * of the commonest flailing there is: try, fail, `ls` to see where you are,
 * try, fail, `cat` the file again, try, fail. Every one of those interleaved
 * looks resets the counter to zero, so a learner can spend ten minutes at a
 * two-thirds failure rate and never trip a signal that exists precisely for
 * them. The streak is kept as well rather than replaced — five wrong ideas in
 * a row with nothing in between is a different state from a slow grind, and
 * the streak sees it sooner.
 *
 * The window has to be full before the ratio is allowed to speak, and that is
 * the whole of what keeps it from quietly lowering `ERROR_STREAK` to four. A
 * rate needs a denominator: four failures out of four commands is a learner
 * who has typed four times, which this file has always said is exploring, and
 * only once six commands have gone by is "four of them failed" a judgement
 * about a rate rather than about a handful of typos.
 */
const ERROR_WINDOW = 6;
const ERROR_WINDOW_FAILURES = 4;

/**
 * No offer at all in the first minute and a half of a level, whatever the
 * counters say. Someone who mistypes five times in the first thirty seconds
 * is finding their keyboard, not drowning, and an app that offers help that
 * early reads as an app that thinks you are stupid.
 */
const MIN_ELAPSED_MS = 90_000;

/** Long enough that the level cannot still be being read, plus a silent minute and a half. */
const QUIET_AFTER_MS = 6 * 60_000;
const QUIET_IDLE_MS = 90_000;

/**
 * Two minutes with nothing typed, saved or checked, counted from the level
 * opening or from the warm-up closing, whichever came last. Level 0's brief is
 * five lines; two minutes is past reading it twice. A longer brief is read in
 * that time as well, and the offer is one line that does not move the
 * terminal, so the cost of being early is a line somebody waves away.
 */
const NOT_STARTED_MS = 2 * 60_000;

/**
 * After the answer itself has been shown, a hint has nothing left to offer,
 * so the offer changes: two more checks that still fail and the way out is
 * the exit, not more help.
 */
const SPENT_CHECKS = 2;

/**
 * The words, in the language the page is in.
 *
 * `getStrings()` rather than `useStrings()`, deliberately: `decideNudge` is a
 * pure function called from a `setInterval` and from three callbacks, none of
 * which is a render, and it is the thing stuck.test.ts argues with. A hook
 * here would either crash outside a component or, worse, be read once and
 * freeze every later nudge in whatever language the tab opened in.
 */
export function decideNudge(s: StuckSignals): Nudge | null {
  if (s.cleared) return null;
  const words = getStrings().progress.nudge;
  const seen = (id: NudgeId) => s.seen.includes(id);
  const laddered = s.hintsUsed < s.hintCount;

  /**
   * The offer a hint-rung signal makes.
   *
   * Three signals below decide *that* a learner is stuck; this decides what to
   * hand them, and there is one thing left to hand them. Null when there is
   * nothing — the ladder is spent, or this particular offer has already been
   * made — so that the caller falls through to the next signal exactly as the
   * earlier `!seen(id)` conditions did.
   */
  const help = (id: NudgeId, w: { text: string; label: string }): Nudge | null =>
    laddered && !seen(id) ? { id, text: w.text, action: 'hint', actionLabel: w.label } : null;

  // Checked in order of how sure each one is, and the first match wins, so a
  // learner is never handed two ways to feel stuck at once.
  if (s.hintsUsed >= s.hintCount && s.repeatedChecks >= SPENT_CHECKS && !seen('ladder-spent')) {
    return {
      id: 'ladder-spent',
      text: words.ladderSpent.text,
      action: 'skip',
      actionLabel: words.ladderSpent.label,
    };
  }

  if (s.elapsedMs < MIN_ELAPSED_MS) return null;

  // Before every other signal, because every other one needs something done
  // first, and a learner who has done nothing is the one most likely to leave
  // (see the file's header). "Check it" would be no use to them: there is
  // nothing there to grade.
  if (!s.started && s.checks === 0 && s.idleMs >= NOT_STARTED_MS && !seen('not-started')) {
    if (s.firstStep === 'iris') {
      return { id: 'not-started', text: words.notStarted.iris.text, action: 'iris', actionLabel: words.notStarted.iris.label };
    }
    const offer = help('not-started', words.notStarted.hint);
    if (offer) return offer;
  }

  // Before anything is spent: a learner who has never pressed Check has not
  // yet been told what is actually wrong. The objectives say which part is
  // not done — for nothing, and without giving anything away — so that is
  // the first offer, and a hint is only the second.
  const failingHard = s.errorStreak >= ERROR_STREAK || (s.recentCommands >= ERROR_WINDOW && s.recentFailures >= ERROR_WINDOW_FAILURES);
  const flailing = failingHard || (s.idleMs >= QUIET_IDLE_MS && s.elapsedMs >= QUIET_AFTER_MS);
  if (s.checks === 0 && flailing && !seen('never-checked')) {
    return {
      id: 'never-checked',
      text: words.neverChecked.text,
      action: 'check',
      actionLabel: words.neverChecked.label,
    };
  }

  // Standing still, either by repeating yourself or by going round in a
  // circle. The two counters are different measurements of the same despair
  // and are offered in the same words, because to the learner they are the
  // same experience: nothing they do moves the level.
  if (s.repeatedChecks >= REPEATED_CHECKS || s.alternatingChecks >= ALTERNATING_CHECKS) {
    const offer = help('same-result', words.sameResult);
    if (offer) return offer;
  }

  if (failingHard) {
    const offer = help('error-streak', words.errorStreak);
    if (offer) return offer;
  }

  if (!s.madeProgress && s.elapsedMs >= QUIET_AFTER_MS && s.idleMs >= QUIET_IDLE_MS) {
    const offer = help('gone-quiet', words.goneQuiet);
    if (offer) return offer;
  }

  return null;
}

export interface StuckApi {
  /** The one line to show, or null when there is nothing worth saying. */
  nudge: Nudge | null;
  /** Hints the app offered rather than the learner asked for. Stars do not count these. */
  freeHints: number;
  /** Whether this level has been open long enough to offer the way out in the panel. */
  mayBail: boolean;
  /** A grade came back. */
  noteCheck(result: GradeResult): void;
  /** A command finished at the prompt. `ok` is false when it exited non-zero, undefined when the guest did not say. */
  noteCommand(ok?: boolean): void;
  /** An editor save: work that never passes through the prompt. */
  noteSave(): void;
  /** The workspace was rebuilt from the start of the level: the counters describe a state that no longer exists. */
  noteReset(): void;
  /** The learner took the offer. The hint it opens is free. */
  accept(): void;
  /** The learner waved it away. It does not come back. */
  dismiss(): void;
}

/**
 * When the way out appears in the Help panel of its own accord.
 *
 * Gating the exit behind "read all five hints" would mean a learner who
 * simply wants to move on has to click through the answer to be allowed to,
 * which is the product extracting a toll for letting someone leave. Ten
 * minutes on one level is long enough that offering the door is a kindness
 * and short enough that nobody sits there deciding whether to close the tab.
 */
const BAIL_AFTER_MS = 10 * 60_000;

/** How often the clock-based rules are re-examined. Slow: nothing here is urgent. */
const TICK_MS = 15_000;

/**
 * The counters, at the start of a level.
 *
 * One function rather than two literals, because the mount and the
 * level-change effect below have to agree about every field, and the day they
 * stopped agreeing would be the day a learner inherited the previous level's
 * despair — silently, and only on the second level of a session.
 *
 * `commands` is the window the error ratio reads: the last `ERROR_WINDOW`
 * outcomes whose exit status the guest actually reported, oldest first.
 * `prevFailing` is the check before `lastFailing`, which is the whole of what
 * alternation needs: A, B, A is the current set equalling the one two back
 * and differing from the one immediately before.
 */
function emptySignals() {
  return {
    openedAt: Date.now(),
    lastActivityAt: Date.now(),
    repeatedChecks: 0,
    alternatingChecks: 0,
    errorStreak: 0,
    commands: [] as boolean[],
    /** Objectives passing at the first check of this level, and the most since. */
    checks: 0,
    started: false,
    firstPassing: null as number | null,
    bestPassing: 0,
    lastFailing: '',
    prevFailing: '',
    seen: [] as NudgeId[],
  };
}

export interface StuckOptions {
  /** Something is in front of the level (the warm-up): decide nothing, and count its closing as activity. */
  paused?: boolean;
  /** See `StuckSignals.firstStep`. */
  firstStep?: 'iris' | 'hint';
}

export function useStuck(
  missionId: string,
  hintsUsed: number,
  hintCount: number,
  cleared: boolean,
  { paused = false, firstStep = 'hint' }: StuckOptions = {},
): StuckApi {
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const [freeHints, setFreeHints] = useState(0);
  const [mayBail, setMayBail] = useState(false);
  /** What is on screen, readable outside a render. See `settle`. */
  const shown = useRef<Nudge | null>(null);

  // Signals live in a ref, not in state: every one of them is written from a
  // callback that fires per command, and a render per command in a terminal
  // someone is typing into is a render per keystroke's worth of work for
  // nothing. The clock below is what publishes.
  const signals = useRef(emptySignals());

  // A new level is a new everything. Play is not remounted between levels
  // (navigation is pushState, browser.md decision 5), so without this the
  // second level of a session would inherit the first one's despair.
  useEffect(() => {
    signals.current = emptySignals();
    shown.current = null;
    setNudge(null);
    setFreeHints(0);
    setMayBail(false);
  }, [missionId]);

  // Time spent in the warm-up is not time spent stuck on the level: the
  // clock for silence starts again when it closes.
  useEffect(() => {
    if (!paused) signals.current.lastActivityAt = Date.now();
  }, [paused]);

  const reconsider = useCallback(() => {
    if (paused) return;
    const s = signals.current;
    const now = Date.now();
    // Independent of the nudges: the door opens on the clock and stays open,
    // whether or not anything was ever offered.
    if (now - s.openedAt >= BAIL_AFTER_MS) setMayBail(true);
    if (shown.current) return; // one nudge at a time; it stays until answered
    const next = decideNudge({
      elapsedMs: now - s.openedAt,
      idleMs: now - s.lastActivityAt,
      repeatedChecks: s.repeatedChecks,
      alternatingChecks: s.alternatingChecks,
      errorStreak: s.errorStreak,
      recentCommands: s.commands.length,
      recentFailures: s.commands.reduce((n, failed) => n + (failed ? 1 : 0), 0),
      checks: s.checks,
      started: s.started,
      firstStep,
      madeProgress: s.firstPassing !== null && s.bestPassing > s.firstPassing,
      hintsUsed,
      hintCount,
      cleared,
      seen: s.seen,
    });
    if (!next) return;
    shown.current = next;
    setNudge(next);
  }, [hintsUsed, hintCount, cleared, paused, firstStep]);

  useEffect(() => {
    if (cleared) return;
    const timer = window.setInterval(reconsider, TICK_MS);
    return () => window.clearInterval(timer);
  }, [reconsider, cleared]);

  const noteCheck = useCallback(
    (result: GradeResult) => {
      const s = signals.current;
      s.lastActivityAt = Date.now();
      s.started = true;
      s.checks += 1;
      const passing = result.objectives.filter((o) => o.passed).length;
      if (s.firstPassing === null) s.firstPassing = passing;
      s.bestPassing = Math.max(s.bestPassing, passing);
      const failing = result.objectives
        .filter((o) => !o.passed && !o.skipped)
        .map((o) => o.id)
        .sort()
        .join(',');
      // "The same result" means the same objectives failing, not the same
      // number of them: a learner who fixes one thing and breaks another has
      // moved, and is not the learner this is looking for.
      s.repeatedChecks = failing !== '' && failing === s.lastFailing ? s.repeatedChecks + 1 : failing === '' ? 0 : 1;
      // …and "going round in a circle" means this set is the one from two
      // checks ago and not the one from the last.
      s.alternatingChecks = nextAlternating(s.alternatingChecks, failing, s.lastFailing, s.prevFailing);
      s.prevFailing = s.lastFailing;
      s.lastFailing = failing;
      reconsider();
    },
    [reconsider],
  );

  const noteCommand = useCallback(
    (ok?: boolean) => {
      const s = signals.current;
      s.lastActivityAt = Date.now();
      s.started = true;
      // `undefined` is an image that does not report exit status. Counting it
      // as a success would be a lie about a learner who may be failing; as a
      // failure it would nudge everybody. It resets the streak instead, which
      // simply means this signal is off on that image.
      s.errorStreak = ok === false ? s.errorStreak + 1 : 0;
      // The window is emptied by the same `undefined`, for the same reason
      // and with the same consequence: on an image that does not report exit
      // status the window never fills, the ratio never has a denominator, and
      // the signal is simply off there rather than guessing.
      if (ok === undefined) s.commands.length = 0;
      else {
        s.commands.push(ok === false);
        if (s.commands.length > ERROR_WINDOW) s.commands.shift();
      }
      reconsider();
    },
    [reconsider],
  );

  const noteSave = useCallback(() => {
    signals.current.lastActivityAt = Date.now();
    signals.current.started = true;
  }, []);

  /**
   * A reset is a fresh start, so the evidence of being stuck goes with it —
   * but not the memory of which offers have been made, which belongs to the
   * learner and the level rather than to the workspace.
   */
  const noteReset = useCallback(() => {
    const s = signals.current;
    s.lastActivityAt = Date.now();
    s.repeatedChecks = 0;
    s.alternatingChecks = 0;
    s.errorStreak = 0;
    s.commands.length = 0;
    s.lastFailing = '';
    s.prevFailing = '';
  }, []);

  /**
   * Answering a nudge, either way. The nudge on screen is read from a ref
   * rather than from a setState updater: an updater that also counted a free
   * hint would count it twice under React's development double-invocation,
   * which is the same trap useProgress documents for XP.
   */
  const settle = useCallback((taken: boolean) => {
    const current = shown.current;
    if (!current) return;
    signals.current.seen = [...signals.current.seen, current.id];
    // Answering one, either way, is activity and a fresh start: the counters
    // that produced it must not immediately produce it again.
    signals.current.lastActivityAt = Date.now();
    signals.current.repeatedChecks = 0;
    signals.current.alternatingChecks = 0;
    signals.current.errorStreak = 0;
    signals.current.commands.length = 0;
    // Only 'hint'. A check costs nothing and the exit gives nothing back, so
    // counting either here would hand back a star for help the ladder never
    // gave.
    if (taken && current.action === 'hint') setFreeHints((n) => n + 1);
    shown.current = null;
    setNudge(null);
  }, []);

  const accept = useCallback(() => settle(true), [settle]);
  const dismiss = useCallback(() => settle(false), [settle]);

  // One object, and the same one until something in it actually changes. The
  // play screen puts this in the dependencies of a callback that a keyboard
  // listener is rebuilt from, so a fresh object every render would mean a
  // listener torn down and re-added on every keystroke's worth of work.
  return useMemo(
    () => ({ nudge, freeHints, mayBail, noteCheck, noteCommand, noteSave, noteReset, accept, dismiss }),
    [nudge, freeHints, mayBail, noteCheck, noteCommand, noteSave, noteReset, accept, dismiss],
  );
}
